import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useCourtById } from '../../../hooks/court/useCourtById'
import { useClubs } from '../../../hooks/clubs/useClubs'
import { useInfiniteLoad } from '../../../hooks/useInfiniteLoad'

import Loading from '../../../components/loading/Loading'
import { Button } from '../../../components/buttons/Button'
import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import { CourtEdit } from '../../../components/authenticated/management/court/courtEdit/CourtEdit'

import { useApp } from '../../../contexts/AppContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { ClubModel, CourtModel } from '../../../types/court/court'


export const Court = () => {
  const navigate = useNavigate()
  const { courtId } = useParams()
  const { showBanner } = useApp()
  const { t } = useTranslation('courts')
  const [court, setCourt] = useState<CourtModel | null>(null)

  const isEditMode = Boolean(courtId)

  const {
    courtById,
    error: courtError,
    resetState: resetCourtState,
  } = useCourtById()

  const {
    clubs,
    error: clubsError,
    resetState: resetClubsState,
  } = useClubs()

  const {
    items: clubsList,
    loading: clubsLoading,
    loadInitial: loadInitialClubs,
    loadMore: handleLoadMoreClubs,
    prependItems: prependClubs,
    reset: resetClubs,
  } = useInfiniteLoad<ClubModel>({
    getItemKey: club => club.id ?? club.name,
    fetchPage: async (page, size) => {
      const response = await clubs(size, page)

      if (!response) {
        return null
      }

      return {
        content: response.content as ClubModel[],
        page: response.pageable.pageNumber,
        last: response.pageable.last,
      }
    },
  })

  useEffect(() => {
    const loadData = async () => {
      resetClubs()
      setCourt(null)
      const parsedCourtId = courtId ? Number(courtId) : null

      if (
        parsedCourtId !== null &&
        (!Number.isInteger(parsedCourtId) || parsedCourtId <= 0)
      ) {
        navigate('/m/courts', { replace: true })
        return
      }

      const [clubsLoaded, courtData] = await Promise.all([
        loadInitialClubs(),
        parsedCourtId !== null
          ? courtById(parsedCourtId)
          : Promise.resolve(null),
      ])

      if (parsedCourtId !== null && !courtData) {
        navigate('/m/courts', { replace: true })
        return
      }

      if (clubsLoaded && courtData?.club) {
        prependClubs([courtData.club])
      }

      if (courtData) {
        setCourt(courtData)
      }
    }

    void loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courtId])

  useEffect(() => {
    const error = courtError || clubsError

    if (!error) {
      return
    }

    showBanner({
      kind: BannerKind.ERR,
      msg: error,
    })
    resetState()
    if (courtError || clubsList === null) {
      navigate('/m/courts', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courtError, clubsError])

  const resetState = () => {
    resetCourtState()
    resetClubsState()
  }

  const handleBack = () => {
    navigate('/m/courts')
  }

  return (
    <>
      <PageHeader
        title={{
          eyebrow: t(
            isEditMode
              ? 'courts.edit.page-header.eyebrow'
              : 'courts.new.page-header.eyebrow'
          ),
          h1:
            court?.name ??
            t(
              isEditMode
                ? 'courts.edit.page-header.title'
                : 'courts.new.page-header.title'
            ),
        }}
        sub={t(
          isEditMode
            ? 'courts.edit.page-header.subtitle'
            : 'courts.new.page-header.subtitle'
        )}
        actions={[
          <Button
            key="back"
            label={t('courts.edit.page-header.back-btn')}
            size="md"
            onClick={handleBack}
          />,
        ]}
      />
      {clubsList === null || (isEditMode && !court) ? (
        <Loading variant="card" />
      ) : (
        <CourtEdit
          court={court}
          clubs={clubsList}
          clubsLoading={clubsLoading}
          isEditMode={isEditMode}
          onLoadMoreClubs={handleLoadMoreClubs}
          onCourtUpdated={setCourt}
        />
      )}
    </>
  )
}
